import { API, BlockTool } from '@editorjs/editorjs';
import { LanguageOption } from './types/language-option';
import { ICodeEditorFactory } from './interfaces/CodeEditorFactory';
import BuildCodeEditorFacade from './facades/build-code-editor-facade';
import { CodeEditorBlockToolData } from './types/data';
import './index.css';

export type CodeEditorBlockConfig = {
  getEditorFactory: () => ICodeEditorFactory;
  languages?: LanguageOption[];
  callbacks?: Partial<{
    onChangeLanguage: (language: string) => void;
    onRunCode: (code: string, language: string) => Promise<string>;
    onClearCode(): void;
  }>;
};

type ConstructorParams = {
  api: API;
  data?: CodeEditorBlockToolData;
  config?: CodeEditorBlockConfig;
};

export default class CodeEditorBlockTool implements BlockTool {
  private readonly data: CodeEditorBlockToolData;
  private readonly config: CodeEditorBlockConfig;
  private readonly api: API;
  private readonly blockBuilder: BuildCodeEditorFacade;
  public readonly wrapper: HTMLElement;
  private readonly codeEditorFactory: ICodeEditorFactory;

  constructor({ data, config, api }: ConstructorParams) {
    if (!config?.getEditorFactory) {
      throw new Error("'codeEditorFactory' config is required");
    }
    this.codeEditorFactory = config.getEditorFactory();
    const firstLanguage = config?.languages?.[0]?.value || '';
    this.data = {
      code: data?.code || '',
      language: data?.language || firstLanguage,
    };

    this.config = config;
    this.api = api;
    this.wrapper = document.createElement('div');
    this.wrapper.setAttribute('id', 'code-editor-wrapper');
    this.blockBuilder = new BuildCodeEditorFacade(
      this.wrapper,
      this.data,
      config,
      this.api,
      this.codeEditorFactory,
    );
  }

  static get toolbox() {
    return {
      title: 'Código',
      icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5">
  <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
</svg>`,
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  save(): CodeEditorBlockToolData {
    return {
      code: this.data.code,
      language: this.data.language,
    };
  }

  private getOutputElement() {
    const executionContainer = this.wrapper.querySelector(
      `#${this.blockBuilder.getExecutionContainerId()}`,
    );
    return executionContainer?.querySelector('#output');
  }

  async runCode() {
    const result = await this.config?.callbacks?.onRunCode?.(
      this.data.code,
      this.data.language,
    );
    if (typeof result !== 'string') return;
    const executionContainer = this.wrapper.querySelector(
      `#${this.blockBuilder.getExecutionContainerId()}`,
    );
    const output = this.getOutputElement();

    if (output) {
      executionContainer?.removeChild(output);
    }
    const resultElement = document.createElement('div');
    resultElement.id = 'output';
    resultElement.innerText = result;
    executionContainer?.append(resultElement);
  }

  async clearCode() {
    const executionContainer = this.wrapper.querySelector(
      `#${this.blockBuilder.getExecutionContainerId()}`,
    );
    const output = this.getOutputElement();
    if (output) {
      executionContainer?.removeChild(output);
    }
    this.config.callbacks?.onClearCode?.();
  }

  changeCode(code: string) {
    this.data.code = code;
  }

  render(): HTMLElement {
    return this.blockBuilder.create({
      onChangeCode: this.changeCode.bind(this),
      onChangeLanguage: this.onChangeLanguage.bind(this),
      onRunCode: this.runCode.bind(this),
      clearCode: this.clearCode.bind(this),
    });
  }

  onChangeLanguage(language: string) {
    this.data.language = language;
    this.config?.callbacks?.onChangeLanguage?.(language);
    this.codeEditorFactory.updateLanguage(language);
  }
}
